// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import ExpandRoundedIcon from '@mui/icons-material/ExpandRounded';
import KeyboardTabRoundedIcon from '@mui/icons-material/KeyboardTabRounded';
import PanToolAltRoundedIcon from '@mui/icons-material/PanToolAltRounded';

// material ui
import {
  alpha,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
} from '@mui/material';

const getToolConfig = (t) => ({
  'baseline-point': {
    label: t('dialogs.shared.tool_menu.baseline_marker'),
    hint: t('dialogs.shared.tool_menu.drop_marker'),
    palette: 'info',
    icon: <KeyboardTabRoundedIcon color="info" />,
  },
  'baseline-range': {
    label: t('common.baseline_period'),
    hint: t('dialogs.shared.tool_menu.drag_to_select'),
    palette: 'info',
    icon: (
      <ExpandRoundedIcon sx={{ transform: 'rotate(90deg)' }} color="info" />
    ),
  },
  'comparison-point': {
    label: t('dialogs.shared.tool_menu.comparison_marker'),
    hint: t('dialogs.shared.tool_menu.drop_marker'),
    palette: 'success',
    icon: <KeyboardTabRoundedIcon color="success" />,
  },
  'comparison-range': {
    label: t('common.comparison_period'),
    hint: t('dialogs.shared.tool_menu.drag_to_select'),
    palette: 'success',
    icon: (
      <ExpandRoundedIcon sx={{ transform: 'rotate(90deg)' }} color="success" />
    ),
  },
});

function ToolAvatar({ tool, size = 36, toolConfig }) {
  if (!tool) {
    return (
      <Avatar
        sx={{
          width: size,
          height: size,
          borderRadius: 2,
          bgcolor: (t) => alpha(t.palette.divider, 0.1),
        }}
      >
        <PanToolAltRoundedIcon fontSize="small" sx={{ color: 'gray' }} />
      </Avatar>
    );
  }
  const cfg = toolConfig[tool];
  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        borderRadius: 2,
        backgroundColor: (t) => alpha(t.palette[cfg.palette].main, 0.1),
      }}
    >
      {cfg.icon}
    </Avatar>
  );
}

export default function ToolMenu({ activeTool, setActiveTool }) {
  const { t } = useTranslation('energyxplorer-initiatives-tab');
  const TOOL_CONFIG = getToolConfig(t);

  const selectTool = (tool) => {
    setActiveTool((t) => (t === tool ? null : tool));
    // handleClose();
  };

  const renderToolItem = (tool) => {
    const selected = activeTool === tool;
    if (!tool) {
      // "None" option
      return (
        <MenuItem
          key="none"
          role="menuitemradio"
          aria-checked={selected}
          selected={selected}
          onClick={() => selectTool(null)}
          sx={{ m: 1, borderRadius: 2 }}
        >
          <ListItemIcon>
            <ToolAvatar tool={null} toolConfig={TOOL_CONFIG} />
          </ListItemIcon>
          <ListItemText
            sx={{ ml: 2 }}
            primary={t('dialogs.shared.tool_menu.no_tool_selected')}
            secondary={t('dialogs.shared.tool_menu.pan_and_zoom')}
          />
        </MenuItem>
      );
    }
    const cfg = TOOL_CONFIG[tool];
    return (
      <MenuItem
        key={tool}
        role="menuitemradio"
        aria-checked={selected}
        selected={selected}
        onClick={() => selectTool(tool)}
        sx={{ m: 1, borderRadius: 2 }}
      >
        <ListItemIcon>
          <Avatar
            sx={{
              mr: 2,
              borderRadius: 3,
              backgroundColor: (t) => alpha(t.palette[cfg.palette].main, 0.1),
            }}
          >
            {cfg.icon}
          </Avatar>
        </ListItemIcon>
        <ListItemText primary={cfg.label} secondary={cfg.hint} />
      </MenuItem>
    );
  };

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
        {renderToolItem('baseline-point')}
        {renderToolItem('baseline-range')}
        <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
        {renderToolItem('comparison-point')}
        {renderToolItem('comparison-range')}
        <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
        {renderToolItem(null)}
      </Stack>
    </>
  );
}
